import { API_CONFIG } from '../config/api';
import {
  PublicSharedPriceHistoryPoint,
  PublicSharedProduct,
  PublicSharedUnderlyingHistorySeries,
} from '../types/productShare';

export type PriceHistoryPeriod = 'day' | 'month' | 'year' | 'all';

export class PublicShareApiError extends Error {
  constructor(
    message: string,
    readonly statusCode?: number
  ) {
    super(message);
    this.name = 'PublicShareApiError';
  }
}

async function parsePublicShareResponse<T>(response: Response): Promise<T> {
  let payload: { success?: boolean; data?: T; message?: string } | null = null;

  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new PublicShareApiError(
      payload?.message || 'Share link not found',
      response.status
    );
  }

  if (!payload?.success || payload.data === undefined) {
    throw new PublicShareApiError(payload?.message || 'Invalid share link response');
  }

  return payload.data;
}

export async function getPublicSharedProduct(token: string): Promise<PublicSharedProduct> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/public/share/${encodeURIComponent(token)}/product`,
    {
      headers: {
        Accept: 'application/json',
      },
    }
  );

  return parsePublicShareResponse<PublicSharedProduct>(response);
}

export async function getPublicSharedPriceHistory(
  token: string,
  period: PriceHistoryPeriod = 'all'
): Promise<PublicSharedPriceHistoryPoint[]> {
  const params = new URLSearchParams();
  params.append('period', period);

  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/public/share/${encodeURIComponent(token)}/price-history?${params.toString()}`,
    {
      headers: {
        Accept: 'application/json',
      },
    }
  );

  return parsePublicShareResponse<PublicSharedPriceHistoryPoint[]>(response);
}

export async function getPublicSharedUnderlyingsPriceHistory(
  token: string,
  period: PriceHistoryPeriod = 'all'
): Promise<PublicSharedUnderlyingHistorySeries[]> {
  const params = new URLSearchParams();
  params.append('period', period);

  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/public/share/${encodeURIComponent(token)}/underlyings-history?${params.toString()}`,
    {
      headers: {
        Accept: 'application/json',
      },
    }
  );

  return parsePublicShareResponse<PublicSharedUnderlyingHistorySeries[]>(response);
}

export function getPublicShareErrorMessage(error: unknown): string {
  if (error instanceof PublicShareApiError) {
    if (error.statusCode === 404) {
      return 'This share link is invalid or has expired.';
    }

    return error.message;
  }

  if (error instanceof Error) {
    const normalized = error.message.toLowerCase();
    if (
      normalized.includes('failed to fetch') ||
      normalized.includes('network request failed') ||
      normalized.includes('networkerror')
    ) {
      return 'Network error. Check your connection and try again.';
    }

    return error.message;
  }

  return 'Could not load the shared product.';
}
